import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type Attendance_ParentalAttendanceRequestsQuery,
  type Attendance_RollbackParentalAttendanceInput,
  type ParentalAttendanceRequestFilter,
  type QueryAdditionalOptions,
  type SaveParentalAttendanceRequest,
  type UpdateParentalAttendanceRequest,
  type UseQueryReturnType,
  type WithdrawParentalAttendanceRequest,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { toast } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useCallback } from 'react';
import { attendanceKeys } from './keys';

const absentRequests = graphql(/* GraphQL */ `
  query attendance_parentalAttendanceRequests(
    $filter: ParentalAttendanceRequestFilter
  ) {
    attendance_parentalAttendanceRequests(filter: $filter) {
      id
      adminNote
      attendanceCode {
        id
        code
        name
        description
      }
      approvedBy {
        firstName
        lastName
      }
      approvedByPartyId
      attendanceCodeId
      contactPartyId
      from
      parentNote
      requestType
      status
      studentPartyId
      to
      classGroup {
        name
        yearGroups {
          name
        }
      }
      contact {
        person {
          avatarUrl
          firstName
          lastName
          type
          title {
            id
            name
            nameTextId
          }
        }
        relationships {
          relationshipType
          studentPartyId
        }
      }
      createdOn
      studentNew {
        person {
          partyId
          firstName
          lastName
          avatarUrl
          type
          title {
            id
            name
            nameTextId
          }
        }
        extensions {
          priority
          aen
          medical
        }
      }
    }
  }
`);

const pendingAbsentRequestCount = graphql(/* GraphQL */ `
  query attendance_parentalRequestCount(
    $filter: String
  ) {
    attendance_parentalRequestCount {
      count
    }
  }
`);

const createAbsentRequest = graphql(/* GraphQL */ `
  mutation attendance_saveParentalAttendanceRequest(
    $input: [SaveParentalAttendanceRequest]
  ) {
    attendance_saveParentalAttendanceRequest(input: $input) {
      id
    }
  }
`);

const updateAbsentRequests = graphql(/* GraphQL */ `
  mutation attendance_updateParentalAttendanceRequest(
    $input: [UpdateParentalAttendanceRequest]
  ) {
    attendance_updateParentalAttendanceRequest(input: $input) {
      id
    }
  }
`);

const withdrawAbsentRequest = graphql(/* GraphQL */ `
  mutation attendance_withdrawParentalAttendanceRequest(
    $input: WithdrawParentalAttendanceRequest
  ) {
    attendance_withdrawParentalAttendanceRequest(input: $input) {
      success
    }
  }
`);

const undoAbsentRequest = graphql(/* GraphQL */ `
  mutation attendance_undoParentalAttendanceRequest(
    $input: Attendance_RollbackParentalAttendanceInput
  ) {
    attendance_undoParentalAttendanceRequest(input: $input) {
      success
    }
  }
`);

const pendingAbsentRequestCountQuery = () => ({
  queryKey: attendanceKeys.absenceRequestCount(),
  queryFn: async () => {
    const { attendance_parentalRequestCount: parentalRequestCount } =
      await gqlClient.request(pendingAbsentRequestCount, {});

    return parentalRequestCount?.count ?? 0;
  },
});

export function usePendingAbsentRequestCount() {
  return useQuery({
    ...pendingAbsentRequestCountQuery(),
    refetchInterval: 1000 * 60 * 5,
  });
}

const absentRequestsQuery = (filter: ParentalAttendanceRequestFilter) => ({
  queryKey: attendanceKeys.absentRequests(filter),
  queryFn: () => gqlClient.request(absentRequests, { filter }),
});

export function useAbsentRequests(
  filter: ParentalAttendanceRequestFilter,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...absentRequestsQuery(filter),
    ...additionalOptions,
    select: useCallback(
      ({
        attendance_parentalAttendanceRequests,
      }: Attendance_ParentalAttendanceRequestsQuery) =>
        attendance_parentalAttendanceRequests.map((request) => ({
          ...request,
          student: request.studentNew,
        })),
      [],
    ),
  });
}

export function getAbsentRequests(filter: ParentalAttendanceRequestFilter) {
  return queryClient.fetchQuery(absentRequestsQuery(filter));
}

export function injectIndividualAbsentRequestToCache(
  request: ReturnTypeFromUseAbsentRequests,
) {
  queryClient.setQueryData(
    absentRequestsQuery({ ids: [request.id] }).queryKey,
    {
      attendance_parentalAttendanceRequests: [request],
    },
  );
}

export function useUpsertAbsentRequest() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: SaveParentalAttendanceRequest[]) =>
      gqlClient.request(createAbsentRequest, { input }),
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
    onSuccess: async (_, [code]) => {
      await queryClient.invalidateQueries({
        queryKey: attendanceKeys.allAbsentRequests(),
      });
      toast.success(
        code?.id
          ? t('common:snackbarMessages.updateSuccess')
          : t('common:snackbarMessages.createSuccess'),
      );
    },
  });
}

export function useUpdateAbsentRequests() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: UpdateParentalAttendanceRequest[]) =>
      gqlClient.request(updateAbsentRequests, { input }),
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: attendanceKeys.allAbsentRequests(),
      });
      toast.success(t('common:snackbarMessages.updateSuccess'));
    },
  });
}

export function useWithdrawAbsentRequest() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: WithdrawParentalAttendanceRequest) =>
      gqlClient.request(withdrawAbsentRequest, { input }),
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
    onSuccess: async (_, code) => {
      await queryClient.invalidateQueries({
        queryKey: attendanceKeys.allAbsentRequests(),
      });
      toast.success(
        code?.id
          ? t('common:snackbarMessages.updateSuccess')
          : t('common:snackbarMessages.createSuccess'),
      );
    },
  });
}

export function useUndoAbsentRequest() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: Attendance_RollbackParentalAttendanceInput) =>
      gqlClient.request(undoAbsentRequest, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: attendanceKeys.allAbsentRequests(),
      });

      toast.success(t('common:snackbarMessages.updateSuccess'));
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export type ReturnTypeFromUseAbsentRequests = UseQueryReturnType<
  typeof useAbsentRequests
>[number];
